// Foil Forge: the card inspection scene.
//
// One hero card held in front of a shelf of others. The hero tracks the pointer
// so that moving the mouse is equivalent to tilting a real card under a lamp,
// which is the only way to actually judge a foil -- a still frame of a
// holographic card tells you nothing about it.

import { GpuContext } from '../gfx/device.ts';
import {
  Renderer, DEFAULT_ENVIRONMENT, DEFAULT_GRADE,
  type CardDraw, type Environment, type Grade, type Scene,
} from '../gfx/renderer.ts';
import { CardLibrary } from '../cards/library.ts';
import { DOMAIN_COLORS, type CardData } from '../cards/types.ts';
import {
  compose, damp, clamp, lookAt, mat4, multiply, perspectiveReverseZ,
  type Mat4, type Vec3,
} from '../gfx/math.ts';

const SHELF_SIZE = 9;
const SHELF_SCALE = 0.46;
const HERO_SCALE = 1.30;

interface CardState {
  card: CardData;
  /** Where the card is heading, and where it currently is. */
  pos: Vec3;
  targetPos: Vec3;
  rot: Vec3;
  targetRot: Vec3;
  scale: number;
  targetScale: number;
  hover: number;
  targetHover: number;
  bend: number;
  targetBend: number;
  bendAxis: [number, number];
  highlight: number;
  targetHighlight: number;
  /** Idle vertical drift, applied at draw time so it never accumulates into targetPos. */
  wobble: number;
  flip: number;
  targetFlip: number;
  seed: number;
  model: Mat4;
}

export interface ShowcaseHud {
  setCard(card: CardData, index: number, total: number): void;
  setStats(text: string): void;
}

export class Showcase {
  private readonly gpu: GpuContext;
  private readonly renderer: Renderer;
  private readonly library: CardLibrary;
  private readonly hud: ShowcaseHud;

  private pool: CardData[];
  private index = 0;

  private hero: CardState;
  private shelf: CardState[] = [];

  private readonly env: Environment;
  private readonly grade: Grade;

  // Pointer, in -1..1 across the canvas.
  private pointer: [number, number] = [0, 0];
  private smoothPointer: [number, number] = [0, 0];
  private pointerInside = false;
  private dragging = false;
  private dragStart: [number, number] = [0, 0];
  private dragRot: [number, number] = [0, 0];

  private cameraDistance = 2.35;
  private targetDistance = 2.35;
  private orbitLight = true;
  private lightPhase = 0.6;
  private foilOverride: number | null = null;

  private readonly proj = mat4();
  private readonly view = mat4();
  private readonly viewProj = mat4();
  private cameraPos: Vec3 = [0, 0, 2.35];

  private running = false;
  private lastTime = 0;
  private frameCount = 0;
  private raf = 0;
  private readonly detach: Array<() => void> = [];

  constructor(gpu: GpuContext, renderer: Renderer, library: CardLibrary, hud: ShowcaseHud) {
    this.gpu = gpu;
    this.renderer = renderer;
    this.library = library;
    this.hud = hud;

    this.env = { ...DEFAULT_ENVIRONMENT };
    this.grade = { ...DEFAULT_GRADE };

    // Lead with the showpieces: epics and alternate arts first, so the first
    // thing on screen is the strongest demonstration of the foil.
    const showpieces = library.filter((c) => c.rarity === 'epic' || c.rarity === 'showcase');
    const rest = library.sample(400, 7, (c) => c.rarity !== 'epic' && c.rarity !== 'showcase');
    this.pool = [...shuffle(showpieces, 11), ...rest];
    if (this.pool.length === 0) throw new Error('No cards with artwork are available.');

    this.hero = this.makeState(this.pool[0]!, 0);
    this.rebuildShelf();
    this.attachInput();
  }

  // -- lifecycle -------------------------------------------------------------

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const raw = (now - this.lastTime) / 1000;
      this.lastTime = now;
      // A long gap means the tab was not being drawn; restart the fps window
      // rather than averaging the stall into it.
      if (raw > 0.25) {
        this.frameCount = 0;
        this.statsSince = now;
      }
      const dt = Math.min(raw, 1 / 15);
      this.update(dt, now / 1000);
      this.draw(now / 1000);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  /** Advances and draws exactly one frame. For debugging a stalled loop. */
  step(dt = 1 / 60): void {
    const now = performance.now();
    this.update(dt, now / 1000);
    this.draw(now / 1000);
  }

  destroy(): void {
    this.stop();
    for (const off of this.detach) off();
    this.detach.length = 0;
  }

  // -- state -----------------------------------------------------------------

  private makeState(card: CardData, seedIndex: number): CardState {
    return {
      card,
      pos: [0, 0, 0], targetPos: [0, 0, 0],
      rot: [0, 0, 0], targetRot: [0, 0, 0],
      scale: HERO_SCALE, targetScale: HERO_SCALE,
      hover: 0, targetHover: 0,
      bend: 0, targetBend: 0,
      bendAxis: [1, 0],
      highlight: 0, targetHighlight: 0,
      wobble: 0,
      flip: 0, targetFlip: 0,
      // A per-card seed keeps every copy's foil grain distinct, the way two
      // pulls of the same card are never stamped identically.
      seed: fract(Math.sin(seedIndex * 12.9898 + card.number * 78.233) * 43758.5453),
      model: mat4(),
    };
  }

  private rebuildShelf(): void {
    const wanted: CardData[] = [];
    for (let i = 1; i <= SHELF_SIZE; i++) {
      wanted.push(this.pool[(this.index + i) % this.pool.length]!);
    }

    this.shelf = wanted.map((card, i) => {
      const s = this.makeState(card, this.index + i + 1);
      const t = (i - (SHELF_SIZE - 1) / 2) / ((SHELF_SIZE - 1) / 2); // -1..1
      // Shallow arc so the row reads as laid out on a surface receding away.
      s.targetPos = [t * 1.52, -0.56 - Math.abs(t) * 0.03, -0.60 - Math.abs(t) * 0.40];
      s.targetRot = [-1.02, -t * 0.30, t * 0.05];
      s.targetScale = SHELF_SCALE;
      s.pos = [...s.targetPos] as Vec3;
      s.rot = [...s.targetRot] as Vec3;
      s.scale = s.targetScale;
      return s;
    });

    // Warm the neighbours so stepping through the pool never shows a placeholder.
    void this.library.preload(wanted);
    this.hud.setCard(this.hero.card, this.index, this.pool.length);
  }

  private select(delta: number): void {
    const n = this.pool.length;
    this.index = ((this.index + delta) % n + n) % n;
    const card = this.pool[this.index]!;

    this.hero = this.makeState(card, this.index);
    // Enter from the side the selection came from, so the motion reads as the
    // shelf advancing rather than a card teleporting in.
    this.hero.pos = [Math.sign(delta) * 0.9, -0.35, -0.6];
    this.hero.rot = [-0.5, -Math.sign(delta) * 0.5, 0];
    this.hero.scale = SHELF_SCALE;
    this.rebuildShelf();
  }

  // -- input -----------------------------------------------------------------

  private attachInput(): void {
    const canvas = this.gpu.canvas;

    const on = <K extends keyof HTMLElementEventMap>(
      target: HTMLElement | Window, type: K,
      fn: (e: HTMLElementEventMap[K]) => void, opts?: AddEventListenerOptions,
    ) => {
      target.addEventListener(type, fn as EventListener, opts);
      this.detach.push(() => target.removeEventListener(type, fn as EventListener, opts));
    };

    const toNdc = (e: PointerEvent | WheelEvent): [number, number] => {
      const r = canvas.getBoundingClientRect();
      return [
        ((e.clientX - r.left) / r.width) * 2 - 1,
        -(((e.clientY - r.top) / r.height) * 2 - 1),
      ];
    };

    on(canvas, 'pointermove', (e) => {
      const p = toNdc(e);
      this.pointer = p;
      this.pointerInside = true;
      if (this.dragging) {
        this.dragRot = [
          (p[1] - this.dragStart[1]) * 1.6,
          (p[0] - this.dragStart[0]) * 2.2,
        ];
      }
    });

    on(canvas, 'pointerleave', () => {
      this.pointerInside = false;
      this.pointer = [0, 0];
    });

    on(canvas, 'pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      this.dragging = true;
      this.dragStart = toNdc(e);
      this.dragRot = [0, 0];
    });

    const endDrag = (e: PointerEvent) => {
      if (!this.dragging) return;
      this.dragging = false;
      this.dragRot = [0, 0];
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
    };
    on(canvas, 'pointerup', endDrag);
    on(canvas, 'pointercancel', endDrag);

    on(canvas, 'wheel', (e) => {
      e.preventDefault();
      if (e.ctrlKey) {
        this.targetDistance = clamp(this.targetDistance + e.deltaY * 0.002, 1.25, 4.5);
      } else {
        this.select(e.deltaY > 0 ? 1 : -1);
      }
    }, { passive: false });

    on(window, 'keydown', (e) => {
      switch (e.key) {
        case 'ArrowRight': case 'd': this.select(1); break;
        case 'ArrowLeft': case 'a': this.select(-1); break;
        case 'ArrowUp': this.select(-SHELF_SIZE); break;
        case 'ArrowDown': this.select(SHELF_SIZE); break;
        case ' ':
          e.preventDefault();
          this.hero.targetFlip = this.hero.targetFlip > 0.5 ? 0 : 1;
          break;
        case 'l': this.orbitLight = !this.orbitLight; break;
        case 'f':
          this.foilOverride = this.foilOverride === null ? 0 : this.foilOverride + 1;
          if (this.foilOverride > 3) this.foilOverride = null;
          break;
        case 'r': this.pool = shuffle(this.pool, (Math.random() * 1e6) | 0); this.select(0); break;
        default: return;
      }
    });

    // Physical tilt on a laptop or phone maps straight onto card tilt.
    const onOrient = (e: DeviceOrientationEvent) => {
      if (e.beta === null || e.gamma === null) return;
      this.pointer = [clamp(e.gamma / 35, -1, 1), clamp((e.beta - 45) / 35, -1, 1)];
      this.pointerInside = true;
    };
    window.addEventListener('deviceorientation', onOrient);
    this.detach.push(() => window.removeEventListener('deviceorientation', onOrient));
  }

  // -- simulation ------------------------------------------------------------

  private update(dt: number, time: number): void {
    this.smoothPointer[0] = damp(this.smoothPointer[0], this.pointer[0], 9, dt);
    this.smoothPointer[1] = damp(this.smoothPointer[1], this.pointer[1], 9, dt);

    const [px, py] = this.smoothPointer;

    // ---- hero -------------------------------------------------------------
    const hero = this.hero;
    hero.targetPos = [px * 0.05, 0.07 + py * 0.03, 0];
    hero.targetScale = HERO_SCALE;
    hero.targetHover = this.pointerInside ? 1 : 0.25;

    const flipAngle = hero.flip * Math.PI;
    hero.targetRot = [
      -py * 0.34 + this.dragRot[0],
      px * 0.42 + this.dragRot[1] + flipAngle,
      -px * 0.05,
    ];

    // A card held between finger and thumb bows away from the pressure point.
    hero.targetBend = this.dragging ? -0.020 : -0.007 * (this.pointerInside ? 1 : 0.4);
    const axisLen = Math.hypot(px, py) || 1;
    hero.bendAxis = [px / axisLen, py / axisLen];
    hero.targetHighlight = this.pointerInside ? 0.25 : 0;


    integrate(hero, dt, 12);
    hero.flip = damp(hero.flip, hero.targetFlip, 7, dt);

    // ---- shelf ------------------------------------------------------------
    for (let i = 0; i < this.shelf.length; i++) {
      const s = this.shelf[i]!;
      // A slow, per-card offset drift keeps the row alive without drawing the
      // eye. It is a draw-time offset, not a change of target: folding it back
      // into targetPos would integrate a sine wave and walk the row apart.
      s.wobble = Math.sin(time * 0.7 + i * 1.7) * 0.006;
      integrate(s, dt, 8);
    }

    // ---- camera -----------------------------------------------------------
    this.cameraDistance = damp(this.cameraDistance, this.targetDistance, 6, dt);
    // A slight orbit gives parallax between the hero and the shelf behind it.
    const camX = px * 0.10;
    const camY = 0.02 + py * 0.06;
    this.cameraPos = [camX, camY, this.cameraDistance];

    perspectiveReverseZ(0.56, this.gpu.aspect, 0.05, this.proj);
    lookAt(this.cameraPos, [camX * 0.3, camY * 0.3 - 0.02, 0], [0, 1, 0], this.view);
    multiply(this.proj, this.view, this.viewProj);

    // ---- light ------------------------------------------------------------
    if (this.orbitLight) this.lightPhase += dt * 0.22;
    // Track the pointer as well, so the highlight is where the user is looking.
    const lx = Math.sin(this.lightPhase) * 0.55 + px * 0.35;
    const ly = 0.45 + Math.cos(this.lightPhase * 0.7) * 0.18 + py * 0.30;
    const lz = 0.85;
    const len = Math.hypot(lx, ly, lz);
    this.env.lightDir = [lx / len, ly / len, lz / len];
    this.env.bgLightPos = [0.5 + lx * 0.22, 0.34 - ly * 0.10];
  }

  private draw(time: number): void {
    const draws: CardDraw[] = [];

    for (const s of this.shelf) draws.push(this.toDraw(s, 0.0));
    draws.push(this.toDraw(this.hero, 1.0));

    const scene: Scene = {
      camera: { viewProj: this.viewProj, position: this.cameraPos },
      cards: draws,
      environment: this.env,
      grade: this.grade,
      time,
    };
    this.renderer.render(scene);

    // Frame rate over a fixed wall-clock window. A frame-count window would
    // fold in whatever gap the browser left while the tab was in the
    // background and report a nonsense number for the next second.
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.statsSince;
    if (elapsed >= 500) {
      const fps = (this.frameCount / elapsed) * 1000;
      this.frameCount = 0;
      this.statsSince = now;
      this.hud.setStats(
        `${fps.toFixed(0)} fps · ${this.gpu.width}x${this.gpu.height} · ${draws.length} cards`,
      );
    }
  }

  private statsSince = performance.now();

  private toDraw(s: CardState, emphasis: number): CardDraw {
    const at: Vec3 = [s.pos[0], s.pos[1] + s.wobble, s.pos[2]];
    compose(at, s.rot, [s.scale, s.scale, s.scale], s.model);

    const tier = this.foilOverride ?? this.library.foilTierFor(s.card);
    const domain = s.card.domains[0];
    const glow = domain ? DOMAIN_COLORS[domain] : ([0.6, 0.6, 0.7] as const);

    return {
      visual: this.library.visualFor(s.card),
      model: s.model,
      foilTier: tier,
      hover: s.hover,
      bend: s.bend,
      bendAxis: s.bendAxis,
      highlight: s.highlight,
      reveal: 1,
      seed: s.seed,
      // The showcase card is held, not placed: it keeps its idle breath.
      settled: 0,
      inHand: 0,
      handDirect: 1,
      handAmbient: 1,
      tint: [glow[0], glow[1], glow[2], emphasis * 0.10 * s.hover],
    };
  }

  // -- exposed controls ------------------------------------------------------

  /** Live lighting rig. Mutate in place; the next frame picks it up. */
  get environment(): Environment {
    return this.env;
  }

  /** Live colour grade. Mutate in place; the next frame picks it up. */
  get grading(): Grade {
    return this.grade;
  }

  get currentCard(): CardData {
    return this.hero.card;
  }

  jumpTo(id: string): void {
    const i = this.pool.findIndex((c) => c.id === id);
    if (i >= 0) this.select(i - this.index);
  }

  setPool(cards: CardData[]): void {
    if (cards.length === 0) return;
    this.pool = cards;
    this.index = 0;
    this.hero = this.makeState(this.pool[0]!, 0);
    this.rebuildShelf();
  }
}

function integrate(s: CardState, dt: number, rate: number): void {
  for (let i = 0; i < 3; i++) {
    s.pos[i] = damp(s.pos[i]!, s.targetPos[i]!, rate, dt);
    s.rot[i] = damp(s.rot[i]!, s.targetRot[i]!, rate, dt);
  }
  s.scale = damp(s.scale, s.targetScale, rate, dt);
  s.hover = damp(s.hover, s.targetHover, rate, dt);
  s.bend = damp(s.bend, s.targetBend, rate, dt);
  s.highlight = damp(s.highlight, s.targetHighlight, rate, dt);
}

const fract = (x: number) => x - Math.floor(x);

function shuffle<T>(items: readonly T[], seed: number): T[] {
  const out = [...items];
  let s = seed >>> 0 || 1;
  for (let i = out.length - 1; i > 0; i--) {
    s ^= s << 13; s >>>= 0;
    s ^= s >> 17;
    s ^= s << 5; s >>>= 0;
    const j = s % (i + 1);
    const a = out[i]!, b = out[j]!;
    out[i] = b; out[j] = a;
  }
  return out;
}
